import { Slot } from '@radix-ui/react-slot'
import React, { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { mergeRefs } from 'utils/ref_utils'

interface PresenceProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  children: React.ReactNode;
  present: boolean;
}

function useIsMounted () {
  const isMountedRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  return function isMounted () {
    return isMountedRef.current
  }
}

function useSynchronousCallbackOnChange (dep: unknown, callback: () => void) {
  const prevDepRef = useRef(dep)

  if (dep !== prevDepRef.current) {
    callback()
  }

  prevDepRef.current = dep
}

/**
 * A primitive component for conditionally rendered content. It waits for
 * any animations to complete before removing the content from the DOM.
 */
export const Presence = forwardRef<HTMLDivElement, PresenceProps>(
  ({ asChild, present, ...props }, forwardedRef) => {
    const [exiting, setExiting] = useState(present)
    const ref = useRef<HTMLDivElement>(null)

    useSynchronousCallbackOnChange(present, () => {
      if (!present) {
        setExiting(true)
      }
    })

    const isMounted = useIsMounted()

    useLayoutEffect(() => {
      if (exiting) {
        const element = ref.current as HTMLElement

        if (!element) {
          setExiting(false)
          return
        }

        const animations = element.getAnimations()

        if (animations.length === 0) {
          setExiting(false)
        } else {
          Promise.all(animations.map((animation) => animation.finished)).then(
            () => {
              if (isMounted()) setExiting(false)
            },
            () => {
              // This will throw if the animations are cancelled while waiting
              // for them to finish. That's fine so we just want to ignore
              // the error.
            }
          )
        }

        return () => {
          animations.forEach((animation) => animation.cancel())
        }
      }
    }, [exiting])

    const Component = asChild ? Slot : 'div'

    if (!present && !exiting) return null

    return (
      <Component {...props} ref={mergeRefs(forwardedRef, ref)} />
    )
  }
)
